import { LOB_FREE_FORM_LABEL } from './appTextConstants';

// Path suffix for pull request deployments (e.g., "/pr1234")
const PR_DEPLOY_PATH = process.env.REACT_APP_PR_DEPLOY_PATH || '';
export const prDeployPath = PR_DEPLOY_PATH;

/**
 * Converts a path for use with browser APIs (window.location, location.assign, window.open)
 *
 * USE THIS for native browser navigation that bypasses TanStack Router.
 * Returns the full absolute path including /edge and PR deployment path.
 *
 * For TanStack Router navigation (Link, router.navigate), just use paths directly
 * without /edge prefix - the router's basepath handles it automatically.
 *
 * @param {string} path - The path to process (e.g., '/edge/results/dashboard/123')
 * @returns {string} The full path with /edge and PR path (e.g., '/edge/pr1896/results/dashboard/123')
 *
 * @example
 * // window.location redirect
 * window.location.href = toBrowserPath('/edge/pc')
 * // Returns: '/edge/pr1896/pc' (in PR deployments) or '/edge/pc' (in production)
 *
 * @example
 * // window.open for new tab
 * window.open(toBrowserPath('/edge/osha-details/123'), '_blank')
 *
 * @example
 * // Web component attributes
 * <zui-shell-user logout-user-url={toBrowserPath('/edge/logout')} />
 */
export const toBrowserPath = (path) => {
  // Strip /edge prefix if present (we'll add it back with PR path)
  let cleanPath = path;
  if (cleanPath.startsWith('/edge/')) {
    cleanPath = cleanPath.substring(5);
  }

  // Build full path: /edge + PR path + clean path
  if (PR_DEPLOY_PATH) {
    // Ensure proper slash handling - add leading slash if PR_DEPLOY_PATH doesn't have one
    const normalizedPrPath =
      PR_DEPLOY_PATH && !PR_DEPLOY_PATH.startsWith('/') ? `/${PR_DEPLOY_PATH}` : PR_DEPLOY_PATH;
    return `/edge${normalizedPrPath}${cleanPath}`;
  }
  return `/edge${cleanPath}`;
};

export const moduleConfig = {
  PC: {
    appCode: 'pc',
    favIcon: `${process.env.PUBLIC_URL}/zywave-favicon.ico`,
    fullName: 'Commercial Lines',
    home: '/pc', // TanStack Router: basepath /edge is automatic
    menuName: 'Commercial lines leads',
    module: 'PC',
    productCode: 5,
    results: '/results/pc', // TanStack Router: basepath /edge is automatic
    searchApiSuffix: 'pnc',
    shortName: 'PC',
    title: 'Commercial Lines',
    userInfoProductCode: 'PC',
  },
  EB: {
    appCode: 'eb',
    favIcon: `${process.env.PUBLIC_URL}/zywave-favicon.ico`,
    fullName: 'Employee Benefits',
    home: '/eb', // TanStack Router: basepath /edge is automatic
    menuName: 'Benefits leads',
    module: 'EB',
    productCode: 7,
    results: '/results/eb', // TanStack Router: basepath /edge is automatic
    searchApiSuffix: 'eb',
    shortName: 'EB',
    title: 'Employee Benefits',
    userInfoProductCode: 'H-W',
  },
  EB_BROKER_SEARCH: {
    shortName: 'EB_BROKER_SEARCH',
  },
};

// Action codes
export const DEL_ACTION_CODE = 1;
export const UPD_ACTION_CODE = 2;
export const REN_ACTION_CODE = 3;

// Results Page SIze
export const PAGE_SIZE = 250;

// Results Views
export const MAP_VIEW = 'MAP';
export const TABLE_VIEW = 'TABLE';

// Search Views
export const SEARCH_VIEW = 'SEARCH';
export const SAVED_SEARCH_VIEW = 'SAVED_SEARCH';

// milliseconds equivalent..
export const FIVE_MINUTES = 1000 * 60 * 5;
export const TEN_MINUTES = 1000 * 60 * 10;

// Maximum possible revenue value constant
export const MAX_REVENUE = 999999999999999;

// Revenue value constant
export const INSIGHT_SEARCH_REVENUE_RANGE = 'insightSearch_revenueRange';

// Employee value constant
export const EMPLOYEE_RANGE_SEARCH_RANGE_TYPE = 'employeeRangeSearch_rangeType';

// Table Header templates
export const HEADER_TEMPLATE_DOT =
  '<span style="vertical-align: sub"><img role="presentation" src="' +
  process.env.PUBLIC_URL +
  '/img/third-party/Seal_of_the_United_States_Department_of_Transportation.svg" height="16"/></span>';
export const HEADER_TEMPLATE_OSHA =
  '<span style="vertical-align: middle"><img role="presentation" src="' +
  process.env.PUBLIC_URL +
  '/img/third-party/US-OSHA-Logo.svg" height="12"/></span>';
export const HEADER_TEMPLATE_WHD =
  '<span style="vertical-align: sub"><img role="presentation" src="' +
  process.env.PUBLIC_URL +
  '/img/third-party/WHD.svg" height="16" width="36" style="padding-top:4px"/></span>';

// Employee Benefits default excluded scopes
export const EB_EXCLUDE_SCOPES = ['F5500', 'F5500 SF'];

export const EB_WELFARE_5500 = 'F5500';
export const EB_RETIREMENT_5500 = 'F5500 SF';
export const EB_NO_5500 = 'F5500 NO';

// Employee Benefits default scopes of welfare and retirement
export const EB_DEFAULT_SCOPES = [
  {
    label: 'Welfare',
    value: EB_WELFARE_5500,
  },
  {
    label: 'Retirement',
    value: EB_RETIREMENT_5500,
  },
];

// Employee Benefits default scope popup menu items
export const EB_SCOPE_ITEMS = [
  {
    label: 'Welfare',
    value: EB_WELFARE_5500,
  },
  {
    label: 'Retirement',
    value: EB_RETIREMENT_5500,
  },
  {
    label: 'Did Not File 5500',
    value: EB_NO_5500,
  },
];

// Global login state enum
export const LOGGED_IN_STATE = {
  UNKNOWN: 0,
  LOGGED_IN: 1,
  LOGGED_OUT: 2,
};

// EB LOB list
// !IMPORTANT - The order of the items in this list is important.
// the order is the order displayed in the UI select dropdown
export const BENEFITS_LOB_ITEMS = [
  { key: 'lob_health', label: 'Health', value: 0 },
  { key: 'lob_stop_loss', label: 'Stop Loss', value: 1 },
  { key: 'lob_dental', label: 'Dental', value: 2 },
  { key: 'lob_vision', label: 'Vision', value: 3 },
  { key: 'lob_life_insurance', label: 'Life Insurance', value: 4 },
  { key: 'lob_long_term_disability', label: 'Long-term Disability', value: 6 },
  { key: 'lob_temporary_disability', label: 'Temporary Disability', value: 5 },
  { key: 'lob_voluntary_all', label: 'Voluntary (All)', value: 7 },
  { key: 'lob_voluntary_life', label: 'Voluntary Life', value: 14 },
  { key: 'lob_voluntary_ltd', label: 'Voluntary LTD', value: 15 },
  { key: 'lob_voluntary_std', label: 'Voluntary STD', value: 16 },
  { key: 'lob_critical_illness', label: 'Voluntary Critical Illness/Cancer', value: 10 },
  { key: 'lob_legal', label: 'Legal', value: 9 },
  { key: 'lob_wellness', label: 'Wellness/EAP', value: 11 },
  { key: 'lob_business_travel', label: 'Business Travel', value: 8 },
  { key: 'lob_statutory_disability', label: 'Statutory Disability', value: 17 },
  { key: 'lob_supplemental_unemployment', label: 'Supplemental Unemployment', value: 12 },
  { key: 'lob_executive', label: 'Executive', value: 18 },
  { key: 'lob_long_term_care', label: 'Long Term Care', value: 13 },
  { key: 'lob_indemnity', label: 'Indemnity', value: 20 },
  { key: 'lob_other', label: 'Other', value: 19 },
];

// Maps a search parameter key to its display label.
export const BENEFIT_INFORMATION_ITEMS = [
  { key: 'benefits_broker_counties', label: 'County' },
  { key: 'broker_states', label: 'Broker state' },
  { key: 'benefits_broker_city', label: 'Benefits broker city' },
  { key: 'benefits_broker', label: 'Benefits broker' },
  { key: 'broker_bins', label: 'Benefits broker(normalized)' },
  { key: 'benefits_broker_address', label: 'Benefits broker address' },
  { key: 'benefits_broker_zip', label: 'Benefits broker zip code' },
  { key: 'lob_free_form', label: LOB_FREE_FORM_LABEL },
  { key: 'states', label: 'Company state' },
  { key: INSIGHT_SEARCH_REVENUE_RANGE, label: 'Revenue range' },
  { key: EMPLOYEE_RANGE_SEARCH_RANGE_TYPE, label: 'Employee range' },
  { key: 'companyNaicsSearch_industry_search_criterias', label: 'NAICS code' },
  { key: 'benefits_plan_renewal_months', label: 'Renewal month' },
  { key: 'benefits_carrier', label: 'Carrier' },
  { key: 'benefits_carriers_normalized', label: 'Carrier (Normalized)' },
];

// Maps a broker search dashboard filter to its display label.
export const BROKER_SEARCH_DASHBOARD_FILTER_ITEMS = [
  { key: 'selectedStates', label: 'Broker state' },
  { key: 'brokerZip', label: 'Zip code' },
  { key: 'lob_free_form', label: LOB_FREE_FORM_LABEL },
  { key: 'selectedCompanyStates', label: 'Company state' },
  { key: INSIGHT_SEARCH_REVENUE_RANGE, label: 'Revenue range' },
  { key: 'employeeRangeSearch_employeeRangeTb', label: 'Employee range' },
  { key: 'benefits_plan_renewal_months', label: 'Renewal month' },
  { key: 'companyNaicsSearch_industry_search_criterias', label: 'NAICS code' },
  { key: 'benefits_carriers_normalized', label: 'Carrier (Normalized)' },
  { key: 'benefits_carrier', label: 'Carrier' },
];

export const EMPLOYEE_RANGE_SEARCH = {
  employeeRange: [0, 10000],
  employeeRangeTb: ['', ''],
  rangeType: 'slider',
};

export const PAGE_SIZE_OPTIONS_BROKER_LOCATIONS = [
  { value: 5, label: '5' },
  { value: 10, label: '10' },
  { value: 25, label: '25' },
  { value: 50, label: '50' },
];

export const PAGE_SIZE_OPTIONS_CONTACTS = [
  { value: 5, label: '5' },
  { value: 10, label: '10' },
  { value: 15, label: '15' },
  { value: 20, label: '20' },
  { value: 25, label: '25' },
];

export const RENEWAL_MONTHS = [
  { key: 1, label: 'January' },
  { key: 2, label: 'February' },
  { key: 3, label: 'March' },
  { key: 4, label: 'April' },
  { key: 5, label: 'May' },
  { key: 6, label: 'June' },
  { key: 7, label: 'July' },
  { key: 8, label: 'August' },
  { key: 9, label: 'September' },
  { key: 10, label: 'October' },
  { key: 11, label: 'November' },
  { key: 12, label: 'December' },
];

// Constants for account type
export const USER_TYPE_SUPER = 1;
export const USER_TYPE_TEST = 2;
export const USER_TYPE_ONLINE = 3;
export const USER_TYPE_ENTERPRISE = 4;
export const USER_TYPE_BACKEND = 5;
export const USER_TYPE_LEGACY = 6;
export const USER_TYPE_ZYWAVE = 7;

// Constants for Zywave License key
export const ZY_LICENSE_KEY_AMS = 'e23a9a79-2493-442d-9a02-f864b756d3c9';

// Constants for Launch Darkly feature toggle
export const LD_AMS_ACCOUNTS_LIST_VIEW = 'ams-accounts-list-view';
export const LD_MIEDGE_ALERTS_ALLOW_VIEWING = 'miEdge-alerts-allow-viewing-alerts';
export const MIEDGE_ALLOW_STATISTICS = 'miedge-allow-statistics';
export const LD_MIEDGE_BROKER_SEARCH_PREMIUM_ALLOCATION_SUMMARY =
  'miedge-broker-search-premium-allocation-summary';
export const LD_MIEDGE_BROKER_SEARCH_FILTER_CONTACT_ROLE_TITLES =
  'miedge-broker-search-filter-contact-role-titles';
export const LD_MIEDGE_BROKER_SEARCH_BROKER_SHARE = 'miedge-broker-search-broker-share';
export const LD_MIEDGE_BROKER_SEARCH_FOR_ALL = 'miedge-broker-search-for-all';
export const LD_MIEDGE_BROKER_SEARCH_EXPORT_LOCATION_BOB =
  'miedge-broker-search-export-location-bob';
export const LD_MIEDGE_BROKER_SEARCH_EXPORT_CONTACTS = 'miedge-broker-search-export-contacts';
export const LD_MIEDGE_BROKER_CLEAR_FILTERS = 'miedge-broker-clear-filters';
export const LD_MIEDGE_UPDATE_EXPIRED_EMAIL_LINK = 'miedge-update-expired-email-link';
export const LD_MIEDGE_PREMIUM_CONTACTS_5500_SIGNER = 'miedge-premium-contacts-5500-signer';

// LocalStorage keys
export const ZYW_LEADS_LOGOUT_REDIRECT = 'ZYWAVE-LEADS:LOGOUT_REDIRECT';
export const ZYW_FEATURE_AGENCIES = 'ZYWAVE-LEADS:FEATURE_AGENCIES';
export const ZYW_LEADS_LOGIN_RETRY_COUNT = 'ZYWAVE-LEADS:LOGIN_RETRY_COUNT';
export const ZYW_LEADS_LOGIN_ERROR_TIMESTAMP = 'ZYWAVE-LEADS:LOGIN_ERROR_TIMESTAMP';
export const ZYW_LEADS_REDIRECT_COUNTER = 'ZYWAVE-LEADS:REDIRECT_COUNTER';
export const ZYW_LEADS_REDIRECT_URL = 'ZYWAVE-LEADS:REDIRECT_URL';
export const ZYW_LEADS_RETRY_FLAG = 'ZYWAVE-LEADS:RETRY_FLAG';

// NAICS Configuration Constants
export const NAICS_MENU_DISPLAY_COUNT = 1;
export const NAICS_BUTTON_TYPE = 'secondary';

// Premium Contacts
export const PREMIUM_CONTACTS_PAGE_SIZE = 25;
export const PREMIUM_CONTACTS_CURRENT_PAGE = 1;

// Page size options for dropdowns
export const PAGE_SIZE_OPTIONS = [
  { value: 5, label: '5' },
  { value: 10, label: '10' },
  { value: 15, label: '15' },
  { value: 20, label: '20' },
  { value: 25, label: '25' },
];
// Constants for Placeholder text
export const PLACEHOLDER_SEARCH = 'Search';
export const PLACEHOLDER_SELECT = 'Select...';
export const PLACEHOLDER_ENTER = 'Enter';
export const ROLE_TITLES = [
  'Account Executive',
  'Account Manager',
  'Agent',
  'Assistant Vice President',
  'Benefits Administrator',
  'Broker',
  'Business Development',
  'Client Executive',
  'Customer Service Representative',
  'Employee Benefits Representative',
  'Insurance Agent',
  'Insurance Broker',
  'Producer',
  'Sales',
  'Sales Director',
  'Vice President',
];
export const ROLE_TITLES_GROUP = {
  'Assistant Vice President': ['Assistant Vice President', 'AVP'],
  'Sales Director': ['Sales Director', 'Director of Sales'],
  'Vice President': ['Vice President', 'VP'],
};
export const MAX_BROKER_SEARCH_CONTACT_EXPORT = 500;

export const DEFAULT_BROKER_FILTER_VALUES = {
  benefits_carrier: '',
  benefits_carriers_normalized: [],
  benefits_carriers_normalized_include: undefined,
  benefits_plan_renewal_months: [],
  brokerZip: '',
  companyNaicsSearch: { industry_search_criterias: [] },
  employeeRangeSearch: EMPLOYEE_RANGE_SEARCH,
  insightSearch: {},
  selectedCompanyStates: [],
  selectedStates: [],
};
